import { useState, useEffect, useRef, useCallback } from 'react';
import { alarmAPI } from '../services/api';
import usePeriodicFetch from './usePeriodicFetch';

/**
 * 새로운 알람을 감지하고 모달로 표시하는 훅
 * @param {Object} user - 사용자 정보
 * @param {number} interval - 알람 체크 간격 (밀리초, 기본값: 30000ms = 30초)
 */
const useAlarmNotification = (user, interval = 30000) => {
  const [newAlarm, setNewAlarm] = useState(null);
  const shownAlarmIdsRef = useRef(new Set());
  const lastCheckTimeRef = useRef(null);
  const isInitializedRef = useRef(false);

  // localStorage에서 이미 본 알람 ID 목록 불러오기
  useEffect(() => {
    if (user?.id) {
      const stored = localStorage.getItem(`shownAlarmIds_${user.id}`);
      if (stored) {
        try {
          const ids = JSON.parse(stored);
          shownAlarmIdsRef.current = new Set(ids);
        } catch (error) {
          console.error('Failed to parse shown alarm IDs:', error);
        }
      } else {
        shownAlarmIdsRef.current = new Set();
      }
      isInitializedRef.current = true;
    }
  }, [user?.id]);

  // 이미 본 알람 ID를 localStorage에 저장
  const saveShownAlarmIds = useCallback((ids) => {
    if (user?.id) {
      try {
        localStorage.setItem(`shownAlarmIds_${user.id}`, JSON.stringify(Array.from(ids)));
      } catch (error) {
        console.error('Failed to save shown alarm IDs:', error);
      }
    }
  }, [user?.id]);

  // 최신 알람 확인
  const checkNewAlarms = useCallback(async () => {
    if (!user?.id || !isInitializedRef.current) return;

    try {
      const condition = {
        userId: user.id,
        cursor: null,
        limit: 1 // 최신 알람 1개만 확인
      };

      const response = await alarmAPI.getAll(condition);
      const alarms = response.alarms || [];

      if (alarms.length > 0) {
        const latestAlarm = alarms[0];

        // 이미 본 알람이면 무시
        if (shownAlarmIdsRef.current.has(latestAlarm.id)) {
          lastCheckTimeRef.current = Date.now();
          return;
        }

        // 읽지 않은 알람만 표시
        if (!latestAlarm.isRead) {
          // 최근 체크 시간 이후에 생성된 알람만 표시 (중복 방지)
          const alarmCreatedAt = new Date(latestAlarm.createdAt).getTime();
          const lastCheckTime = lastCheckTimeRef.current || Date.now() - interval;
          
          if (alarmCreatedAt > lastCheckTime) {
            setNewAlarm(latestAlarm);
            // 표시한 알람 ID 추가
            shownAlarmIdsRef.current.add(latestAlarm.id);
            saveShownAlarmIds(shownAlarmIdsRef.current);
          }
        }
      }

      lastCheckTimeRef.current = Date.now();
    } catch (error) {
      console.error('알람 확인 실패:', error);
    }
  }, [user?.id, interval, saveShownAlarmIds]);

  // 주기적으로 알람 확인
  usePeriodicFetch(
    checkNewAlarms,
    interval,
    [user?.id],
    !!user?.id // 사용자가 있을 때만 활성화
  );

  // 모달 닫기
  const closeModal = useCallback(() => {
    setNewAlarm(null);
  }, []);

  // 알람 읽음 처리
  const markAlarmAsRead = useCallback((alarmId) => {
    // 이미 본 알람 목록에 추가
    shownAlarmIdsRef.current.add(alarmId);
    saveShownAlarmIds(shownAlarmIdsRef.current);
  }, [saveShownAlarmIds]);

  return {
    newAlarm,
    closeModal,
    markAlarmAsRead
  };
};

export default useAlarmNotification;

